import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import client from "../lib/api";
import { MapTrifold, WarningCircle } from "@phosphor-icons/react";

/**
 * LiveTruckMap.
 *
 * OpenStreetMap + Leaflet live GPS view. Pulls the Samsara vehicles/
 * locations endpoint on mount and renders a truck-icon marker for
 * every vehicle with valid lat/lng. Auto-fits map bounds so ops sees
 * their whole fleet without panning.
 *
 * Auto-hides when:
 *   - Samsara not connected (silent — the connect card is elsewhere)
 *   - Zero vehicles with GPS data
 *
 * Uses OSM tiles (free, no API key). Upgrade to Mapbox for satellite
 * view later — the dark theme reads well against the app's aesthetic.
 */

// Custom SVG truck icon (orange, matches app accent color)
const _TRUCK_ICON = L.divIcon({
  className: "custom-truck-marker",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
  html: `
    <div style="
      width: 28px; height: 28px;
      background: #FF5A1F;
      border: 2px solid #0A0A0A;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(255,90,31,0.5);
      display: flex; align-items: center; justify-content: center;
      color: #0A0A0A; font-weight: 900; font-size: 11px;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    ">◆</div>
  `,
});

// Component that auto-fits map bounds to all markers
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0][0], points[0][1]], 10);
      return;
    }
    map.fitBounds(points, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch {
    return "";
  }
}

export default function LiveTruckMap() {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [pulseKey, setPulseKey] = useState(0);

  useEffect(() => {
    let alive = true;

    // One fetch pass. Called on mount + every 30s so ops sees trucks
    // drift live without a manual refresh. Errors on the periodic pass
    // stay silent (we keep the last-known good frame on screen); the
    // very first mount surfaces errors so a broken ELD token doesn't
    // silently render an empty map.
    async function tick(isInitial) {
      try {
        const { data: st } = await client.get("/eld/status");
        if (!alive) return;
        setStatus(st);
        if (!st?.any_connected) {
          if (isInitial) setLoading(false);
          return;
        }
        const { data: locs } = await client.get("/eld/locations");
        if (!alive) return;
        setData(locs);
        setRefreshedAt(new Date());
        setPulseKey((k) => k + 1); // trigger the ping animation
        if (isInitial && !locs?.ok) setError(locs?.error || "Fetch failed");
      } catch (e) {
        if (alive && isInitial) {
          setError(e?.response?.data?.detail || "GPS fetch failed");
        }
        // periodic-pass errors: silently keep the last frame on screen
      } finally {
        if (alive && isInitial) setLoading(false);
      }
    }

    tick(true);
    const intervalId = setInterval(() => tick(false), 30_000);
    return () => { alive = false; clearInterval(intervalId); };
  }, []);

  if (loading) return null;
  if (!status?.any_connected) return null;

  const vehicles = (data?.vehicles || []).filter(
    (v) => Number.isFinite(v.latitude) && Number.isFinite(v.longitude),
  );

  if (vehicles.length === 0 && !error) return null;

  const points = vehicles.map((v) => [v.latitude, v.longitude]);

  return (
    <div data-testid="live-truck-map" className="card-surface p-0 overflow-hidden">
      <div className="flex items-center gap-2.5 p-4 border-b border-white/5">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
          <MapTrifold size={18} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold flex items-center gap-1.5">
            {/* Pulsing dot = the poll cycle heartbeat. Re-keyed on every
                successful refresh so it visibly flashes each tick. */}
            <span
              key={pulseKey}
              data-testid="live-truck-map-pulse"
              className="relative inline-flex h-2 w-2"
            >
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF5A1F] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF5A1F]" />
            </span>
            ELD · Live map
          </div>
          <div className="font-display text-lg font-semibold text-white leading-tight">
            {vehicles.length} truck{vehicles.length === 1 ? "" : "s"} on the road
          </div>
        </div>
        {refreshedAt && (
          <div
            className="text-[10px] text-neutral-500 font-mono shrink-0"
            title={`Auto-refreshes every 30s · last update ${refreshedAt.toLocaleTimeString()}`}
            data-testid="live-truck-map-refreshed-at"
          >
            {timeAgo(refreshedAt.toISOString())}
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 flex items-start gap-2 text-xs text-red-300 border-b border-red-500/20 bg-red-500/[0.03]">
          <WarningCircle size={14} className="mt-0.5 shrink-0" />
          <div>ELD GPS fetch failed: {error}</div>
        </div>
      )}

      {vehicles.length > 0 && (
        <div className="h-96 relative" data-testid="live-truck-map-container">
          <MapContainer
            style={{ height: "100%", width: "100%" }}
            center={[points[0][0], points[0][1]]}
            zoom={5}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={points} />
            {vehicles.map((v) => (
              <Marker
                key={v.samsara_id}
                position={[v.latitude, v.longitude]}
                icon={_TRUCK_ICON}
              >
                <Popup>
                  <div style={{ fontFamily: "-apple-system, sans-serif", minWidth: "180px" }}>
                    <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "4px" }}>
                      {v.name || `Truck ${v.samsara_id.slice(0, 6)}`}
                    </div>
                    {v.reverse_geo && (
                      <div style={{ fontSize: "11px", color: "#555", marginBottom: "2px" }}>
                        {v.reverse_geo}
                      </div>
                    )}
                    {v.speed_mph !== null && v.speed_mph !== undefined && (
                      <div style={{ fontSize: "11px", color: "#333" }}>
                        <b>{Math.round(v.speed_mph)} mph</b>
                        {v.heading_deg !== null && v.heading_deg !== undefined && (
                          <> · {Math.round(v.heading_deg)}° heading</>
                        )}
                      </div>
                    )}
                    {v.time && (
                      <div style={{ fontSize: "10px", color: "#888", marginTop: "4px" }}>
                        Updated {timeAgo(v.time)}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
}
